import React, { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { prefetchLotusCasinoCore } from '../../lib/lotusCasinoPrefetch'
import PromoBannerCarousel from '../PromoBannerCarousel/PromoBannerCarousel'
import LotusCasinoHeaderTabs from '../LotusCasinoHeaderTabs/LotusCasinoHeaderTabs'
import LotusCasinoLobby from '../LotusCasinoLobby/LotusCasinoLobby'
import LotusCasinoLobbyNav from '../LotusCasinoLobby/LotusCasinoLobbyNav'
import LotusCasinoTabGames from '../LotusCasinoTabGames/LotusCasinoTabGames'
import LotusCasinoProviderGames from '../LotusCasinoProviderGames/LotusCasinoProviderGames'
import LotusCasinoFavoritesView from './LotusCasinoFavoritesView'
import {
  LOTUS_CASINO_FAVORITES_PATH,
  LOTUS_PROVIDERS_PATH,
} from '../../constants/mainCasinoRoute'
import {
  getLotusTabByIndex,
  parseLotusTabIndexFromPath,
} from '../../constants/lotusCasinoTabs'
import './LotusCasinoShell.css'
import '../LotusCasinoLobby/LotusCasinoLobby.css'
import './LotusCasinoRecommendedSection.css'

export type LotusCasinoShellProps = {
  children?: React.ReactNode
  preLoginMode?: boolean
  activeTabIndex?: number | null
  embeddedCategory?: string | null
  embeddedTabIndex?: number | null
  embeddedView?: string | null
  onEmbeddedCategoryChange?: (category: string) => void
  onEmbeddedViewChange?: (view: string | null) => void
  onEmbeddedTabChange?: (tabIndex: number) => void
}

const LotusCasinoShell: React.FC<LotusCasinoShellProps> = ({
  children = null,
  preLoginMode = false,
  activeTabIndex,
  embeddedCategory,
  embeddedTabIndex,
  embeddedView,
  onEmbeddedCategoryChange,
  onEmbeddedViewChange,
  onEmbeddedTabChange,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const view = preLoginMode ? embeddedView : searchParams.get('view')

  useEffect(() => {
    prefetchLotusCasinoCore(queryClient, preLoginMode)
  }, [queryClient, preLoginMode])

  const routeTabIndex = activeTabIndex ?? parseLotusTabIndexFromPath(location.pathname)
  const resolvedTabIndex = preLoginMode ? embeddedTabIndex : routeTabIndex
  const activeTab =
    typeof resolvedTabIndex === 'number' ? getLotusTabByIndex(resolvedTabIndex) : undefined
  const isRouteOnly = activeTab?.targetType === 'route' && !activeTab.casinoType

  const categoryParam = preLoginMode ? embeddedCategory : searchParams.get('category')
  const gameTypeParam = preLoginMode ? null : searchParams.get('gameType')

  const isProviderGamePage =
    Boolean(categoryParam) &&
    !gameTypeParam &&
    view !== 'favorites' &&
    view !== 'providers' &&
    (preLoginMode
      ? embeddedTabIndex == null
      : /^\/casino-int\/[^/]+$/.test(location.pathname) && searchParams.has('category'))

  const isLobbyTab = resolvedTabIndex === 1 && view !== 'favorites' && !isProviderGamePage
  const showFavorites = view === 'favorites' && !preLoginMode
  const showCategoryTab =
    typeof resolvedTabIndex === 'number' &&
    resolvedTabIndex > 1 &&
    !showFavorites &&
    view !== 'providers' &&
    !isRouteOnly &&
    !isProviderGamePage

  const hideGamesGrid = isLobbyTab || showFavorites || showCategoryTab || isProviderGamePage

  const handleProviderBack = () => {
    if (preLoginMode) {
      onEmbeddedViewChange?.('providers')
      onEmbeddedCategoryChange?.('')
    }
  }

  return (
    <div
      className={`lotus-casino-shell${isLobbyTab ? ' is-lobby-mode' : ''}${showCategoryTab ? ' is-tab-mode' : ''}${isProviderGamePage ? ' is-provider-mode' : ''}`}
    >
      <div className="lotus-casino-shell__layout-content">
        {!isProviderGamePage && (
          <div className="lotus-casino-shell__banner">
            <PromoBannerCarousel />
          </div>
        )}

        {!isProviderGamePage && (
          <LotusCasinoHeaderTabs
            preLoginMode={preLoginMode}
            embeddedCategory={embeddedCategory}
            embeddedTabIndex={embeddedTabIndex}
            embeddedView={embeddedView}
            activeTabIndex={resolvedTabIndex}
            onEmbeddedCategoryChange={onEmbeddedCategoryChange}
            onEmbeddedViewChange={onEmbeddedViewChange}
            onEmbeddedTabChange={onEmbeddedTabChange}
          />
        )}

        {!isProviderGamePage && view !== 'providers' && (
          <LotusCasinoLobbyNav
            preLoginMode={preLoginMode}
            onFavorites={() => {
              if (preLoginMode) {
                onEmbeddedViewChange?.('favorites')
                return
              }
              navigate(LOTUS_CASINO_FAVORITES_PATH)
            }}
            onProviders={() => {
              if (preLoginMode) {
                onEmbeddedViewChange?.('providers')
                onEmbeddedCategoryChange?.('')
                return
              }
              navigate(LOTUS_PROVIDERS_PATH)
            }}
          />
        )}

        {isProviderGamePage && (
          <LotusCasinoProviderGames
            preLoginMode={preLoginMode}
            embeddedCategory={embeddedCategory}
            onBack={preLoginMode ? handleProviderBack : undefined}
          />
        )}

        {showFavorites && <LotusCasinoFavoritesView />}

        {isLobbyTab && (
          <LotusCasinoLobby
            preLoginMode={preLoginMode}
            onMoreGames={(tabIndex) => {
              if (preLoginMode && onEmbeddedTabChange) {
                onEmbeddedTabChange(tabIndex)
                onEmbeddedViewChange?.(null)
              }
            }}
          />
        )}

        {showCategoryTab && resolvedTabIndex != null && (
          <LotusCasinoTabGames
            tabIndex={resolvedTabIndex}
            preLoginMode={preLoginMode}
            onMoreGames={(tabIndex, groupId) => {
              if (preLoginMode && onEmbeddedTabChange) {
                onEmbeddedTabChange(tabIndex)
                onEmbeddedViewChange?.(null)
              }
            }}
          />
        )}

        <div className={`lotus-casino-shell__games${hideGamesGrid ? ' is-hidden' : ''}`}>
          {children}
        </div>
      </div>
    </div>
  )
}

export default LotusCasinoShell

export function getEmbeddedCategoryForTab(tabIndex: number): string | null {
  const tab = getLotusTabByIndex(tabIndex)
  if (!tab || tab.targetType !== 'category') return null
  return tab.target
}
